/**
 * RenderErrorBoundary — minimal resilience wrapper.
 *
 * If a page section throws during render or commit (e.g. a third-party
 * extension or a DOM-mutating script corrupts React's node references),
 * React unmounts the WHOLE tree and the visitor gets a blank page.
 * This boundary keeps the surrounding layout (header, footer, nav) intact and
 * shows a short recovery message instead — a partial page beats a white page.
 */
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional label used in the console warning for easier debugging. */
  name?: string;
}

interface State {
  hasError: boolean;
}

export class RenderErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[RenderErrorBoundary${this.props.name ? `:${this.props.name}` : ''}]`, error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h2 className="text-xl font-semibold text-foreground">
            This section could not be displayed
          </h2>
          <p className="mt-2 text-muted-foreground">
            Please reload the page. If the problem persists, try disabling browser
            extensions or automatic page translation.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 rounded-lg bg-primary px-5 py-2.5 font-semibold text-primary-foreground"
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default RenderErrorBoundary;
